import type { Card, Effect, Model, Msg, TagDef, TagValue, ViewExprField } from "./types";
import { createEditorState, serialize } from "./editor";
import { insertViewBlockTr, updateViewBlockTr } from "./editor/view-block";
import { insertLinkTr } from "./editor/link";
import { cancelExtract, finishExtract, startExtract } from "./editor/extract";
import { defaultTagValue, draftOf, formatValue, parseDraft, parseValue, systemTagToDef } from "./editor/values";

export const initialModel: Model = {
  route: "card",
  currentCardId: null,
  cardCache: {},
  missingCards: {},
  unreadableCards: {},
  editorStates: {},
  searchQuery: "",
  searchResults: [],
  searchDropdownOpen: false,
  searchSelected: 0,
  modal: null,
  viewBlockDialog: null,
  linkDialog: null,
  editingTag: null,
  tagPicker: null,
  homeCardId: null,
  deleteConfirmId: null,
  registry: [],
  registryHash: "",
  systemTags: [],
  registryDraft: [],
  registryConflicts: null,
  tokens: [],
  tokenDraftName: "",
  tokenDraftScope: "read_write",
  newTokenSecret: null,
  toasts: [],
  toastSeq: 0,
  now: Date.now(),
};

// The new token secret leaves the model with the page that shows it, so a
// later visit to the settings page never shows it again.
export function update(msg: Msg, model: Model): [Model, Effect[]] {
  const [next, effects] = step(msg, model);
  if (next.route !== "settings" && next.newTokenSecret !== null) {
    return [{ ...next, newTokenSecret: null }, effects];
  }
  return [next, effects];
}

function step(msg: Msg, model: Model): [Model, Effect[]] {
  switch (msg.type) {
    case "GoHome": {
      if (model.homeCardId) {
        const [m, fx] = navigateTo(model, model.homeCardId);
        return [m, [...fx, { type: "ClearUrlCard" }]];
      }
      return [{ ...model, route: "card", currentCardId: null, editingTag: null, tagPicker: null }, [{ type: "ClearUrlCard" }]];
    }

    case "NavigateTo": {
      const [m, fx] = navigateTo(model, msg.id);
      return [m, [...fx, { type: "SetUrlCard", id: msg.id }]];
    }

    case "LoadCard": {
      // A card in the cache or in missingCards needs no request.
      if (model.cardCache[msg.id] || model.missingCards[msg.id]) return [model, []];
      return [model, [{ type: "FetchCard", id: msg.id }]];
    }

    case "CardMissing": {
      return [{ ...model, missingCards: { ...model.missingCards, [msg.id]: true } }, []];
    }

    case "CardLoaded": {
      const cardCache = { ...model.cardCache, [msg.card.id]: msg.card };
      const { [msg.card.id]: _found, ...missingCards } = model.missingCards;
      const next = { ...model, cardCache, missingCards };
      // Only the current card fetches its backlinks, so that the fetches do not
      // spread across the graph.
      const effects = msg.card.id === model.currentCardId ? fetchBacklinks(next, msg.card) : [];
      // Each loaded card gets an editor state, because a section of any card
      // is an editor. A reload (external edit) replaces the state; a first load
      // builds it.
      if (!msg.reload && model.editorStates[msg.card.id]) return [next, effects];
      return [withEditorState(next, msg.card), effects];
    }

    case "SaveCard": {
      return [model, [{ type: "WriteCard", id: msg.id, ...msg.patch }]];
    }

    case "CardSaved": {
      const existing = model.cardCache[msg.id];
      if (!existing) return [model, []];
      return [
        {
          ...model,
          cardCache: {
            ...model.cardCache,
            [msg.id]: { ...existing, stateHash: msg.stateHash },
          },
        },
        [],
      ];
    }

    case "OpenModal": {
      return [
        { ...model, modal: { searchQuery: "", results: [] } },
        [{ type: "SearchCards", query: "", target: "modal", tagKey: "template" }],
      ];
    }

    case "CloseModal": {
      return [{ ...model, modal: null }, []];
    }

    case "ModalSearch": {
      if (!model.modal) return [model, []];
      return [
        { ...model, modal: { ...model.modal, searchQuery: msg.query } },
        [{ type: "SearchCards", query: msg.query, target: "modal", tagKey: "template" }],
      ];
    }

    case "ModalSearchResults": {
      if (!model.modal) return [model, []];
      return [
        { ...model, modal: { ...model.modal, results: msg.results } },
        [],
      ];
    }

    case "CreateCard": {
      const createEffect: Effect = msg.templateId
        ? { type: "PutCard", templateId: msg.templateId }
        : { type: "PutCard" };
      return [{ ...model, modal: null }, [createEffect]];
    }

    case "SearchQuery": {
      if (!msg.query) {
        return [
          { ...model, searchQuery: "", searchResults: [], searchDropdownOpen: false, searchSelected: 0 },
          [],
        ];
      }
      return [
        { ...model, searchQuery: msg.query, searchDropdownOpen: true, searchSelected: 0 },
        [{ type: "SearchCards", query: msg.query, target: "search", orderByRelevance: true, limit: 10 }],
      ];
    }

    case "SearchResults": {
      if (msg.query !== model.searchQuery) return [model, []];
      return [
        { ...model, searchResults: msg.results, searchDropdownOpen: true, searchSelected: 0 },
        [],
      ];
    }

    case "CloseSearch": {
      return [
        { ...model, searchDropdownOpen: false },
        [],
      ];
    }

    case "MoveSearchSelection": {
      const n = model.searchResults.length;
      if (n === 0) return [model, []];
      const next = Math.min(Math.max(model.searchSelected + msg.delta, 0), n - 1);
      return [{ ...model, searchSelected: next }, []];
    }

    case "EditTagValue": {
      // A press on a value while another editor is open commits that editor
      // first. Thus the commit does not depend on the blur of its input.
      const [committed, effects] = commitTagEdit(model);
      const card = currentCard(committed);
      const def = tagDefOf(committed, msg.key);
      if (!card || !def) return [committed, effects];
      return [{ ...committed, editingTag: { key: msg.key, draft: draftOf(def, card.tags[msg.key]) } }, effects];
    }

    case "CommitTagEdit": {
      // A late blur of an editor that is already closed has no effect.
      if (model.editingTag?.key !== msg.key) return [model, []];
      return commitTagEdit(model);
    }

    case "EditTagDraft": {
      if (!model.editingTag) return [model, []];
      return [{ ...model, editingTag: { ...model.editingTag, draft: msg.draft } }, []];
    }

    case "SetTagValue": {
      const card = currentCard(model);
      const closed = { ...model, editingTag: null };
      return card ? writeTags(closed, card, { ...card.tags, [msg.key]: msg.value }) : [closed, []];
    }

    case "CancelTagEdit": {
      return [{ ...model, editingTag: null }, []];
    }

    case "DeleteTag": {
      const card = currentCard(model);
      if (!card) return [model, []];
      const { [msg.key]: _deleted, ...tags } = card.tags;
      const editingTag = model.editingTag?.key === msg.key ? null : model.editingTag;
      return writeTags({ ...model, editingTag }, card, tags);
    }

    case "OpenTagPicker": {
      return [{ ...model, tagPicker: { draft: "" }, editingTag: null }, []];
    }

    case "SetTagPickerDraft": {
      if (!model.tagPicker) return [model, []];
      return [{ ...model, tagPicker: { draft: msg.draft } }, []];
    }

    case "CloseTagPicker": {
      return [{ ...model, tagPicker: null }, []];
    }

    case "PickTag": {
      const card = currentCard(model);
      const def = tagDefOf(model, msg.name);
      const closed = { ...model, tagPicker: null };
      // Reject picking a nonexistent tag or one already on the card.
      if (!card || !def || msg.name in card.tags) return [closed, []];
      return writeTags(closed, card, { ...card.tags, [msg.name]: defaultTagValue(def) });
    }

    case "GlobalLoaded": {
      const withHome = { ...model, homeCardId: msg.homeCardId };
      // On first load with no card open, jump to the home card.
      if (!model.currentCardId && msg.homeCardId) {
        return navigateTo(withHome, msg.homeCardId);
      }
      return [withHome, []];
    }

    case "ToggleHomeCard": {
      const homeCardId = model.homeCardId === msg.id ? null : msg.id;
      return [{ ...model, homeCardId }, [{ type: "PutGlobal", homeCardId }]];
    }

    case "RequestDeleteCard": {
      return [{ ...model, deleteConfirmId: msg.id }, []];
    }

    case "CancelDelete": {
      return [{ ...model, deleteConfirmId: null }, []];
    }

    case "ConfirmDeleteCard": {
      return [{ ...model, deleteConfirmId: null }, [{ type: "DeleteCard", id: msg.id }]];
    }

    case "CardDeleted": {
      const cardCache = { ...model.cardCache };
      delete cardCache[msg.id];
      const editorStates = { ...model.editorStates };
      delete editorStates[msg.id];
      const unreadableCards = { ...model.unreadableCards };
      delete unreadableCards[msg.id];
      return [
        {
          ...model,
          cardCache,
          missingCards: { ...model.missingCards, [msg.id]: true },
          unreadableCards,
          editorStates,
          homeCardId: model.homeCardId === msg.id ? null : model.homeCardId,
          currentCardId: model.currentCardId === msg.id ? null : model.currentCardId,
          deleteConfirmId: null,
        },
        [{ type: "DestroyEditor", id: msg.id }],
      ];
    }

    case "EditorTx": {
      return applyEditorTx(model, msg.cardId, msg.tr);
    }

    case "OpenViewBlockDialog": {
      return [
        { ...model, viewBlockDialog: { ...msg.dialog, errors: emptyExprErrors() } },
        [],
      ];
    }

    case "SetViewBlockField": {
      const d = model.viewBlockDialog;
      if (!d) return [model, []];
      const form = { ...d.form, [msg.key]: msg.value };
      let errors = d.errors;
      const effects: Effect[] = [];
      if (isExprField(msg.key)) {
        // Kanban's "group field" is a plain tag name, not a CEL expression.
        const plainField = msg.key === "groupBy" && form.viewType === "kanban";
        if (plainField) {
          errors = { ...errors, groupBy: "" };
        } else {
          // Validation — including clearing the error when the field is
          // emptied — is centralized in the CheckExpr effect, so a stale
          // in-flight check can't land after the field is cleared.
          effects.push({ type: "CheckExpr", field: msg.key, expr: msg.value });
        }
      }
      return [{ ...model, viewBlockDialog: { ...d, form, errors } }, effects];
    }

    case "ViewBlockExprChecked": {
      const d = model.viewBlockDialog;
      if (!d) return [model, []];
      return [
        { ...model, viewBlockDialog: { ...d, errors: { ...d.errors, [msg.field]: msg.error ?? "" } } },
        [],
      ];
    }

    case "CloseViewBlockDialog": {
      return [{ ...model, viewBlockDialog: null }, []];
    }

    case "CommitViewBlock": {
      const d = model.viewBlockDialog;
      const state = d ? model.editorStates[d.cardId] : null;
      const cleared = { ...model, viewBlockDialog: null };
      if (!d || !state) return [cleared, []];
      const tr =
        d.mode === "edit" && d.pos != null
          ? updateViewBlockTr(state, d.pos, d.form)
          : insertViewBlockTr(state, d.form);
      return applyEditorTx(cleared, d.cardId, tr);
    }

    case "OpenLinkDialog": {
      return [{ ...model, linkDialog: msg.dialog }, []];
    }

    case "SetLinkField": {
      const d = model.linkDialog;
      if (!d) return [model, []];
      return [{ ...model, linkDialog: { ...d, [msg.key]: msg.value } }, []];
    }

    case "CloseLinkDialog": {
      return [{ ...model, linkDialog: null }, []];
    }

    case "CommitLink": {
      const d = model.linkDialog;
      const state = d ? model.editorStates[d.cardId] : null;
      const cleared = { ...model, linkDialog: null };
      if (!d || !state || !d.url.trim()) return [cleared, []];
      const tr = insertLinkTr(state, d.from, d.to, d.url.trim(), d.text);
      return applyEditorTx(cleared, d.cardId, tr);
    }

    case "ExtractToCard": {
      const state = model.editorStates[msg.cardId];
      const extract = state ? startExtract(state) : null;
      if (!extract) return [model, []];
      const [next, effects] = applyEditorTx(model, msg.cardId, extract.tr);
      return [next, [...effects, { type: "CreateExtractedCard", cardId: msg.cardId, content: extract.content }]];
    }

    case "CardExtracted": {
      const state = model.editorStates[msg.cardId];
      const tr = state ? finishExtract(state, msg.newId) : null;
      return tr ? applyEditorTx(model, msg.cardId, tr) : [model, []];
    }

    case "ExtractFailed": {
      const state = model.editorStates[msg.cardId];
      return state ? applyEditorTx(model, msg.cardId, cancelExtract(state)) : [model, []];
    }

    case "OpenTagsPage": {
      return [
        { ...model, route: "tags", registryDraft: model.registry.map(cloneDef), registryConflicts: null },
        [{ type: "FetchRegistry" }],
      ];
    }

    case "OpenSettingsPage": {
      return [{ ...model, route: "settings" }, [{ type: "FetchTokens" }]];
    }

    case "TokensLoaded": {
      return [{ ...model, tokens: msg.tokens }, []];
    }

    case "SetTokenDraftName": {
      return [{ ...model, tokenDraftName: msg.name }, []];
    }

    case "SetTokenDraftScope": {
      return [{ ...model, tokenDraftScope: msg.scope }, []];
    }

    case "CreateToken": {
      const name = model.tokenDraftName.trim();
      if (!name) return [model, []];
      return [
        model,
        [{ type: "PostToken", name, scope: model.tokenDraftScope }],
      ];
    }

    case "TokenCreated": {
      return [
        {
          ...model,
          tokens: [...model.tokens, msg.token],
          tokenDraftName: "",
          newTokenSecret: msg.secret,
        },
        [],
      ];
    }

    case "DismissNewToken": {
      return [{ ...model, newTokenSecret: null }, []];
    }

    case "DeleteToken": {
      return [model, [{ type: "DeleteToken", id: msg.id }]];
    }

    case "TokenDeleted": {
      return [{ ...model, tokens: model.tokens.filter((t) => t.id !== msg.id) }, []];
    }

    case "RegistryLoaded": {
      return [
        {
          ...model,
          registry: msg.tags,
          registryHash: msg.hash,
          registryDraft: msg.tags.map(cloneDef),
          systemTags: msg.systemTags,
        },
        [],
      ];
    }

    case "RegistryAddTag": {
      const fresh: TagDef = { name: "", valueType: "string", values: [], allowAll: true };
      return [{ ...model, registryDraft: [...model.registryDraft, fresh] }, []];
    }

    case "RegistryDeleteTag": {
      return [
        { ...model, registryDraft: model.registryDraft.filter((_, i) => i !== msg.index) },
        [],
      ];
    }

    case "RegistrySetName": {
      return [updateDraft(model, msg.index, (d) => ({ ...d, name: msg.name })), []];
    }

    case "RegistrySetType": {
      return [updateDraft(model, msg.index, (d) => ({ ...d, valueType: msg.valueType })), []];
    }

    case "RegistrySetAllowAll": {
      return [
        updateDraft(model, msg.index, (d) =>
          // Can only disable "allow all" when a value list exists to restrict to.
          !msg.allowAll && d.values.length === 0 ? d : { ...d, allowAll: msg.allowAll }
        ),
        [],
      ];
    }

    case "RegistryAddValue": {
      if (!msg.value.trim()) return [model, []];
      return [
        updateDraft(model, msg.index, (d) => {
          // Parse to the structured value; reject non-conforming, dedupe by form.
          const v = parseValue(d.valueType, msg.value);
          if (v === null || d.values.some((x) => formatValue(x) === formatValue(v))) return d;
          return { ...d, values: [...d.values, v] };
        }),
        [],
      ];
    }

    case "RegistryRemoveValue": {
      return [
        updateDraft(model, msg.index, (d) => {
          const values = d.values.filter((_, i) => i !== msg.valueIndex);
          // Emptying the list forces "allow all" back on (closed enum needs ≥1).
          return { ...d, values, allowAll: values.length === 0 ? true : d.allowAll };
        }),
        [],
      ];
    }

    case "RegistrySave": {
      return [model, [{ type: "PutRegistry", tags: model.registryDraft, lastStateHash: model.registryHash }]];
    }

    case "RegistryReset": {
      return [{ ...model, registryDraft: model.registry.map(cloneDef), registryConflicts: null }, []];
    }

    case "RegistrySaved": {
      return [
        {
          ...model,
          registry: msg.tags,
          registryHash: msg.hash,
          registryDraft: msg.tags.map(cloneDef),
          registryConflicts: null,
        },
        [],
      ];
    }

    case "RegistryConflicts": {
      // Rejected: drop the edits and surface the blocking pages.
      return [
        { ...model, registryConflicts: msg.conflicts, registryDraft: model.registry.map(cloneDef) },
        [],
      ];
    }

    case "DismissRegistryConflicts": {
      return [{ ...model, registryConflicts: null }, []];
    }

    case "ShowToast": {
      // Cap the stack so repeated failures don't bury the screen.
      const toast = { id: model.toastSeq, message: msg.message };
      return [
        { ...model, toasts: [...model.toasts, toast].slice(-4), toastSeq: model.toastSeq + 1 },
        [],
      ];
    }

    case "Tick": {
      return [{ ...model, now: msg.now }, []];
    }

    case "DismissToast": {
      return [{ ...model, toasts: model.toasts.filter((t) => t.id !== msg.id) }, []];
    }
  }
}

function cloneDef(d: TagDef): TagDef {
  return { ...d, values: [...d.values] };
}

function updateDraft(model: Model, index: number, f: (d: TagDef) => TagDef): Model {
  return { ...model, registryDraft: model.registryDraft.map((d, i) => (i === index ? f(d) : d)) };
}

// The user can set a registry tag or an assignable system tag (e.g. template).
// This function finds the definition of such a tag.
function tagDefOf(model: Model, name: string): TagDef | undefined {
  const sys = model.systemTags.find((t) => t.name === name && t.assignable);
  return model.registry.find((d) => d.name === name) ?? (sys ? systemTagToDef(sys) : undefined);
}

// Close the open tag editor. Store the draft if it is valid and differs from the
// current value, else discard it.
function commitTagEdit(model: Model): [Model, Effect[]] {
  const editing = model.editingTag;
  const card = currentCard(model);
  const def = editing ? tagDefOf(model, editing.key) : undefined;
  const closed = { ...model, editingTag: null };
  if (!editing || !card || !def) return [closed, []];
  const value = parseDraft(def, editing.draft);
  if (!value || editing.draft === draftOf(def, card.tags[editing.key])) return [closed, []];
  return writeTags(closed, card, { ...card.tags, [editing.key]: value });
}

function currentCard(model: Model): Card | null {
  return model.currentCardId ? (model.cardCache[model.currentCardId] ?? null) : null;
}

// Put new tags of a card into the cache, and write them to the server.
function writeTags(model: Model, card: Card, tags: Record<string, TagValue>): [Model, Effect[]] {
  return [
    { ...model, cardCache: { ...model.cardCache, [card.id]: { ...card, tags } } },
    [{ type: "WriteCard", id: card.id, tags }],
  ];
}

// Apply a ProseMirror transaction to the Model's EditorState for a card. On doc
// change, refresh the cached content and emit a debounced save. Shared by the
// EditorTx msg (typing) and dialog commits.
function applyEditorTx(
  model: Model,
  cardId: string,
  tr: import("prosemirror-state").Transaction
): [Model, Effect[]] {
  const state = model.editorStates[cardId];
  if (!state) return [model, []];
  const newState = state.apply(tr);
  const editorStates = { ...model.editorStates, [cardId]: newState };
  if (!tr.docChanged) {
    return [{ ...model, editorStates }, []];
  }
  const content = serialize(newState.doc);
  const card = model.cardCache[cardId];
  const cardCache = card
    ? { ...model.cardCache, [cardId]: { ...card, content } }
    : model.cardCache;
  return [
    { ...model, editorStates, cardCache },
    [{ type: "WriteCard", id: cardId, content }],
  ];
}

function navigateTo(model: Model, id: string): [Model, Effect[]] {
  const cached = model.cardCache[id];
  const effects: Effect[] = cached ? fetchBacklinks(model, cached) : [{ type: "FetchCard", id }];
  const base: Model = {
    ...model,
    route: "card",
    currentCardId: id,
    editingTag: null,
    tagPicker: null,
    // Navigating away blanks the search bar and its dropdown.
    searchQuery: "",
    searchResults: [],
    searchDropdownOpen: false,
    searchSelected: 0,
  };
  return [base, effects];
}

// Fetches the cards that link to the card, so that its backlinks show their
// titles.
function fetchBacklinks(model: Model, card: Card): Effect[] {
  return card.backlinks
    .filter((id) => !model.cardCache[id] && !model.missingCards[id])
    .map((id) => ({ type: "FetchCard", id }));
}

// Build the editor state of a card. If the editor cannot read the content, the
// card gets no editor state, and the UI shows the reason.
function withEditorState(model: Model, card: Card): Model {
  const { [card.id]: _state, ...editorStates } = model.editorStates;
  const { [card.id]: _reason, ...unreadableCards } = model.unreadableCards;
  try {
    editorStates[card.id] = createEditorState(card.content);
  } catch (err) {
    unreadableCards[card.id] = err instanceof Error ? err.message : String(err);
  }
  return { ...model, editorStates, unreadableCards };
}

const EXPR_FIELDS: ViewExprField[] = ["filter", "orderBy", "groupBy"];

function isExprField(key: string): key is ViewExprField {
  return (EXPR_FIELDS as string[]).includes(key);
}

function emptyExprErrors(): Record<ViewExprField, string> {
  return { filter: "", orderBy: "", groupBy: "" };
}
